import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type FormEvent,
} from "react";
import { getSchoolId } from "../../services/auth";
import { fetchSchool, type SchoolApiRecord } from "../../services/schoolApi";
import {
  fetchBoardSubjects,
  fetchOrgSubjects,
  addOrgSubject,
  removeOrgSubject,
  type SubjectApiRecord,
} from "../../services/subjectApi";
import type { ApiError } from "../../services/api";
import "./SubjectMaster.css";

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type BoardOption = NonNullable<SchoolApiRecord["boards"]>[number];

type PaginationMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

const PAGE_SIZE_OPTIONS = [15, 25, 50];

type DrawerMode = null | "create" | "edit";
interface FormErrors {
  name?: string;
  code?: string;
}

export default function SubjectMaster() {
  /* ---- school / board context ---- */
  const [schoolId] = useState(() => getSchoolId());
  const [boards, setBoards] = useState<BoardOption[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);

  /* ---- data ---- */
  const [subjects, setSubjects] = useState<SubjectApiRecord[]>([]);
  const [boardOrgMap, setBoardOrgMap] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successExiting, setSuccessExiting] = useState(false);
  const [errorExiting, setErrorExiting] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  /* ---- pagination ---- */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [meta, setMeta] = useState<PaginationMeta>({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  /* ---- drawer ---- */
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  /* ---- form ---- */
  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [formIsCore, setFormIsCore] = useState(true);
  const [formIsActive, setFormIsActive] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ---- enable-subjects toggle ---- */
  const [enabledSubjectIds, setEnabledSubjectIds] = useState<Set<string>>(
    new Set(),
  );
  const [togglingId, setTogglingId] = useState<string | null>(null);

  /* ---- delete ---- */
  const [deleteTarget, setDeleteTarget] = useState<SubjectApiRecord | null>(
    null,
  );
  const [editCreatedAt, setEditCreatedAt] = useState("");
  const [editUpdatedAt, setEditUpdatedAt] = useState("");

  /* ============== Toast helpers ============== */
  const dismissSuccess = useCallback(() => {
    setSuccessExiting(true);
    setTimeout(() => {
      setSuccessMsg("");
      setSuccessExiting(false);
    }, 280);
  }, []);

  const dismissError = useCallback(() => {
    setErrorExiting(true);
    setTimeout(() => {
      setErrorMsg("");
      setErrorExiting(false);
    }, 280);
  }, []);

  const showSuccess = useCallback(
    (msg: string) => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      setSuccessMsg(msg);
      setSuccessExiting(false);
      successTimerRef.current = setTimeout(dismissSuccess, 5000);
    },
    [dismissSuccess],
  );

  const showError = useCallback(
    (msg: string) => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      setErrorMsg(msg);
      setErrorExiting(false);
      errorTimerRef.current = setTimeout(dismissError, 5000);
    },
    [dismissError],
  );

  /* ============== Load school boards ============== */
  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const school = await fetchSchool(schoolId);
        const schoolBoards = school.boards ?? [];
        setBoards(schoolBoards);
        if (schoolBoards.length > 0) {
          setSelectedBoardId(schoolBoards[0].id);
          setPage(1);
        } else {
          setLoading(false);
        }
      } catch (err) {
        const apiErr = err as ApiError;
        showError(apiErr.message || "Failed to load school details.");
        setLoading(false);
      }
    })();
  }, [schoolId, showError]);

  /* ============== Debounce search ============== */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  /* ============== Load subjects for selected board ============== */
  const loadSubjects = useCallback(
    async (boardId: number, pg: number, size: number, q: string) => {
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await fetchBoardSubjects(boardId, {
          page: pg,
          perPage: size,
          search: q || undefined,
        });
        setSubjects(res.data);
        setMeta(res.meta);
      } catch (err) {
        const apiErr = err as ApiError;
        showError(apiErr.message || "Failed to load subjects.");
      } finally {
        setLoading(false);
      }
    },
    [showError],
  );

  useEffect(() => {
    if (selectedBoardId != null) {
      loadSubjects(selectedBoardId, page, pageSize, debouncedSearch);
    }
  }, [selectedBoardId, loadSubjects, page, pageSize, debouncedSearch]);

  /* ============== Load org subjects ============== */
  const loadOrgSubjects = useCallback(async () => {
    try {
      const org = await fetchOrgSubjects({ perPage: 200 });
      const enabledIds = new Set<string>();
      const boardOrgMap: Record<number, string> = {};
      for (const o of org) {
        if (o.subject?.id != null) {
          boardOrgMap[o.subject.id] = o.id;
          enabledIds.add(`${o.subject.id}`);
        }
      }
      setEnabledSubjectIds(enabledIds);
      setBoardOrgMap(boardOrgMap);
    } catch (err) {
      const apiErr = err as ApiError;
      showError(apiErr.message || "Failed to load organization subjects.");
    }
  }, [showError]);

  useEffect(() => {
    loadOrgSubjects();
  }, [loadOrgSubjects]);

  const handleToggleSubject = async (s: SubjectApiRecord) => {
    if (togglingId) return;
    const enabling = !enabledSubjectIds.has(`${s.id}`);
    setTogglingId(s.id);
    if (!enabling) {
      await removeOrgSubject(boardOrgMap[parseInt(s.id)]);
      // Toggling off — just update UI for now
      setEnabledSubjectIds((prev) => {
        const next = new Set(prev);
        next.delete(`${s.id}`);
        return next;
      });
      setTogglingId(null);
      return;
    }

    // Toggling on — POST to create org subject

    try {
      await addOrgSubject(s.id);
      showSuccess(`${s.name} added to organization.`);
      await loadOrgSubjects();
    } catch (err) {
      const apiErr = err as ApiError;
      showError(apiErr.message || "Failed to add subject to organization.");
    } finally {
      setTogglingId(null);
    }
  };

  /* ============== Sorted subjects: enabled first ============== */
  const sortedSubjects = useMemo(() => {
    const enabled: SubjectApiRecord[] = [];
    const disabled: SubjectApiRecord[] = [];
    for (const s of subjects) {
      if (enabledSubjectIds.has(`${s.subject_id}`)) enabled.push(s);
      else disabled.push(s);
    }
    return [...enabled, ...disabled];
  }, [subjects, enabledSubjectIds]);

  /* ============== Pagination helpers ============== */
  const pageNumbers = (): number[] => {
    const pages: number[] = [];
    const start = Math.max(1, meta.current_page - 2);
    const end = Math.min(meta.last_page, meta.current_page + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const from = (meta.current_page - 1) * meta.per_page + 1;
  const to = Math.min(meta.current_page * meta.per_page, meta.total);

  /* ============== Form helpers ============== */
  const resetForm = useCallback(() => {
    setFormName("");
    setFormCode("");
    setFormIsCore(true);
    setFormIsActive(true);
    setErrors({});
    setEditingId(null);
    setEditCreatedAt("");
    setEditUpdatedAt("");
  }, []);

  const openCreate = () => {
    resetForm();
    setDrawerMode("create");
  };
  const openEdit = (s: SubjectApiRecord) => {
    resetForm();
    setFormName(s.name);
    setFormCode(s.code);
    setFormIsCore(s.is_core_subject);
    setFormIsActive(s.is_active);
    setEditingId(s.id);
    setEditCreatedAt(s.created_at);
    setEditUpdatedAt(s.updated_at);
    setDrawerMode("edit");
  };
  const closeDrawer = () => {
    setDrawerMode(null);
    resetForm();
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!formName.trim()) e.name = "Subject name is required.";
    if (!formCode.trim()) e.code = "Subject code is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    const payload = {
      name: formName.trim(),
      code: formCode.trim().toUpperCase(),
      is_core_subject: formIsCore,
      is_active: formIsActive,
    };
    await new Promise((r) => setTimeout(r, 600));
    if (drawerMode === "edit" && editingId) {
      setSubjects((prev) =>
        prev.map((s) =>
          s.id === editingId
            ? { ...s, ...payload, updated_at: new Date().toISOString() }
            : s,
        ),
      );
      showSuccess("Subject updated successfully!");
    } else {
      setSubjects((prev) => [
        ...prev,
        {
          id: String(Date.now()),
          subject_id: 0,
          ...payload,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ]);
      showSuccess("Subject created successfully!");
    }
    setIsSubmitting(false);
    closeDrawer();
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setSubjects((prev) => prev.filter((s) => s.id !== deleteTarget.id));
    setDeleteTarget(null);
    showSuccess("Subject removed successfully!");
  };

  /* ============== No school selected ============== */
  if (!schoolId) {
    return (
      <div className="subjects">
        <div className="subjects__header">
          <div className="subjects__header-text">
            <h1 className="subjects__title">Subjects</h1>
            <p className="subjects__subtitle">
              Manage subject master list for all classes. Once you add subjects
            </p>
          </div>
        </div>
        <div className="subjects__table-wrap">
          <div className="subjects__empty">
            <div className="subjects__empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <h3 className="subjects__empty-title">No school selected</h3>
            <p className="subjects__empty-text">
              Please select a school from the top bar to view subjects.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subjects">
      {successMsg && (
        <div
          className={`subjects__toast${successExiting ? " subjects__toast--exit" : ""}`}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span className="subjects__toast-msg">{successMsg}</span>
          <button
            className="subjects__toast-close"
            onClick={dismissSuccess}
            aria-label="Dismiss"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {errorMsg && (
        <div
          className={`subjects__toast subjects__toast--error${errorExiting ? " subjects__toast--exit" : ""}`}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span className="subjects__toast-msg">{errorMsg}</span>
          <button
            className="subjects__toast-close"
            onClick={dismissError}
            aria-label="Dismiss"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      <div className="subjects__header">
        <div className="subjects__header-text">
          <h1 className="subjects__title">Subjects</h1>
          <p className="subjects__subtitle">
            Manage subject master list for all classes.
          </p>
        </div>
        <div className="subjects__header-actions">
          {boards.length > 1 && (
            <select
              className="subjects__search"
              value={selectedBoardId ?? ""}
              onChange={(e) => {
                setSelectedBoardId(Number(e.target.value));
                setPage(1);
              }}
            >
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
          <div className="subjects__search-wrap">
            <svg
              className="subjects__search-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="subjects__search"
              type="text"
              placeholder="Search subjects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="subjects__add-btn" onClick={openCreate}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Subject
          </button>
        </div>
      </div>

      {/* ============= Board Subjects Table ============= */}
      <div className="subjects__table-wrap">
        {loading ? (
          <div className="subjects__loading">
            <div className="subjects__loader">
              <span className="subjects__loader-dot" />
              <span className="subjects__loader-dot" />
              <span className="subjects__loader-dot" />
            </div>
          </div>
        ) : boards.length === 0 ? (
          <div className="subjects__empty">
            <div className="subjects__empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <h3 className="subjects__empty-title">No board affiliated</h3>
            <p className="subjects__empty-text">
              This school has no board affiliation. Add a board to the school to
              manage subjects.
            </p>
          </div>
        ) : subjects.length === 0 ? (
          <div className="subjects__empty">
            <div className="subjects__empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <h3 className="subjects__empty-title">
              {search ? "No subjects match" : "No subjects yet"}
            </h3>
            <p className="subjects__empty-text">
              {search
                ? "Try adjusting your search."
                : "Add your first subject to begin."}
            </p>
          </div>
        ) : (
          <table className="subjects__table">
            <thead>
              <tr>
                <th className="subjects__th">Subject Name</th>
                <th className="subjects__th">Code</th>
                <th className="subjects__th">Type</th>
                <th className="subjects__th">Status</th>
                <th className="subjects__th">Enabled</th>
                <th className="subjects__th subjects__th--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedSubjects.map((s) => {
                return (
                  <tr className="subjects__table-row" key={s.id}>
                    <td className="subjects__td subjects__td--name">
                      {s.name}
                    </td>
                    <td className="subjects__td subjects__td--code">
                      {s.code}
                    </td>
                    <td className="subjects__td">
                      <span
                        className={`subjects__badge ${s.is_core_subject ? "subjects__badge--core" : "subjects__badge--elective"}`}
                      >
                        {s.is_core_subject ? "Core" : "Elective"}
                      </span>
                    </td>
                    <td className="subjects__td">
                      <span
                        className={`subjects__badge ${s.is_active ? "subjects__badge--active" : "subjects__badge--inactive"}`}
                      >
                        {s.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="subjects__td">
                      <div className="subjects__toggle-cell">
                        {togglingId === s.id ? (
                          <svg
                            className="subjects__spinner subjects__spinner--inline"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle cx="12" cy="12" r="10" strokeWidth="3" />
                          </svg>
                        ) : (
                          <label className="subjects__toggle">
                            <input
                              type="checkbox"
                              checked={enabledSubjectIds.has(`${s.id}`)}
                              onChange={() => handleToggleSubject(s)}
                              disabled={togglingId !== null}
                            />
                            <span className="subjects__toggle-track" />
                          </label>
                        )}
                      </div>
                    </td>
                    <td className="subjects__td subjects__td--actions">
                      <button
                        className="subjects__row-btn"
                        onClick={() => openEdit(s)}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        className="subjects__row-btn subjects__row-btn--delete"
                        style={{ marginLeft: 4 }}
                        onClick={() => setDeleteTarget(s)}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- Pagination ---- */}
      {!loading && meta.total > 0 && (
        <div className="subjects__pagination">
          <div className="subjects__pagination-left">
            <label className="subjects__page-size-label">
              Rows per page
              <select
                className="subjects__page-size"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <span className="subjects__page-info">
              Showing {from}–{to} of {meta.total}
            </span>
          </div>

          <div className="subjects__pagination-right">
            <button
              className="subjects__page-btn"
              disabled={meta.current_page <= 1}
              onClick={() => setPage(1)}
              title="First page"
            >
              «
            </button>
            <button
              className="subjects__page-btn"
              disabled={meta.current_page <= 1}
              onClick={() => setPage((p) => p - 1)}
              title="Previous page"
            >
              ‹
            </button>
            {pageNumbers().map((n) => (
              <button
                key={n}
                className={`subjects__page-btn ${n === meta.current_page ? "subjects__page-btn--active" : ""}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              className="subjects__page-btn"
              disabled={meta.current_page >= meta.last_page}
              onClick={() => setPage((p) => p + 1)}
              title="Next page"
            >
              ›
            </button>
            <button
              className="subjects__page-btn"
              disabled={meta.current_page >= meta.last_page}
              onClick={() => setPage(meta.last_page)}
              title="Last page"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Drawer */}
      {drawerMode && (
        <>
          <div className="subjects__overlay" onClick={closeDrawer} />
          <aside className="subjects__drawer">
            <div className="subjects__drawer-header">
              <h2 className="subjects__drawer-title">
                {drawerMode === "edit" ? "Edit Subject" : "Add Subject"}
              </h2>
              <button
                className="subjects__drawer-close"
                onClick={closeDrawer}
                title="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form
              className="subjects__drawer-body"
              onSubmit={handleSubmit}
              noValidate
              id="subject-form"
            >
              <div className="subjects__card">
                <h3 className="subjects__card-title">Subject Details</h3>
                <div className="subjects__field">
                  <label
                    className="subjects__label subjects__label--required"
                    htmlFor="subjName"
                  >
                    Subject Name
                  </label>
                  <input
                    className={`subjects__input ${errors.name ? "subjects__input--error" : ""}`}
                    type="text"
                    id="subjName"
                    placeholder="e.g. Mathematics"
                    value={formName}
                    onChange={(e) => {
                      setFormName(e.target.value);
                      if (errors.name)
                        setErrors((p) => ({ ...p, name: undefined }));
                    }}
                    autoFocus
                  />
                  {errors.name && (
                    <p className="subjects__error">{errors.name}</p>
                  )}
                </div>
                <div className="subjects__field">
                  <label
                    className="subjects__label subjects__label--required"
                    htmlFor="subjCode"
                  >
                    Subject Code
                  </label>
                  <input
                    className={`subjects__input ${errors.code ? "subjects__input--error" : ""}`}
                    type="text"
                    id="subjCode"
                    placeholder="e.g. MATH"
                    value={formCode}
                    onChange={(e) => {
                      setFormCode(e.target.value);
                      if (errors.code)
                        setErrors((p) => ({ ...p, code: undefined }));
                    }}
                    maxLength={10}
                  />
                  {errors.code && (
                    <p className="subjects__error">{errors.code}</p>
                  )}
                </div>
              </div>

              <div className="subjects__card">
                <h3 className="subjects__card-title">Settings</h3>
                <div className="subjects__toggle-row">
                  <div className="subjects__toggle-info">
                    <span className="subjects__toggle-label">Core Subject</span>
                    <span className="subjects__toggle-desc">
                      Core subjects are mandatory for all students
                    </span>
                  </div>
                  <label className="subjects__toggle">
                    <input
                      type="checkbox"
                      checked={formIsCore}
                      onChange={(e) => setFormIsCore(e.target.checked)}
                    />
                    <span className="subjects__toggle-track" />
                  </label>
                </div>
                <div className="subjects__toggle-row">
                  <div className="subjects__toggle-info">
                    <span className="subjects__toggle-label">Active</span>
                    <span className="subjects__toggle-desc">
                      Inactive subjects are hidden from allocations
                    </span>
                  </div>
                  <label className="subjects__toggle">
                    <input
                      type="checkbox"
                      checked={formIsActive}
                      onChange={(e) => setFormIsActive(e.target.checked)}
                    />
                    <span className="subjects__toggle-track" />
                  </label>
                </div>
              </div>

              {drawerMode === "edit" && editCreatedAt && (
                <div className="subjects__timestamps">
                  <span className="subjects__timestamp">
                    Created: {formatTimestamp(editCreatedAt)}
                  </span>
                  <span className="subjects__timestamp">
                    Updated: {formatTimestamp(editUpdatedAt)}
                  </span>
                </div>
              )}
            </form>
            <div className="subjects__drawer-footer">
              <button
                className="subjects__submit"
                type="submit"
                form="subject-form"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="subjects__spinner"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle cx="12" cy="12" r="10" strokeWidth="3" />
                    </svg>
                    {drawerMode === "edit" ? "Updating…" : "Creating…"}
                  </>
                ) : drawerMode === "edit" ? (
                  "Update Subject"
                ) : (
                  "Create Subject"
                )}
              </button>
              <button
                className="subjects__cancel"
                type="button"
                onClick={closeDrawer}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div
          className="subjects__confirm-overlay"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="subjects__confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="subjects__confirm-title">Remove Subject</h3>
            <p className="subjects__confirm-text">
              Are you sure you want to remove{" "}
              <strong>{deleteTarget.name}</strong>? This action can be undone by
              an administrator.
            </p>
            <div className="subjects__confirm-actions">
              <button
                className="subjects__confirm-cancel"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="subjects__confirm-delete"
                onClick={confirmDelete}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
